import { useMemo, useState } from "react";
import {
  useParams,
  useRouteContext,
  useNavigate,
} from "@tanstack/react-router";
import { type GridColumnProps } from "@progress/kendo-react-grid";
import LayoutContentArea from "@/components/layout/LayoutContentArea";
import Card from "@/components/shared/Card";
import KendoTableClientState from "@/components/shared/tables/KendoTableClientState";
import { useCertifications } from "@/queries";
import {
  parseCertificationMonth,
  normalizeStatus,
} from "@/utils/certifications";
import {
  CertificationSearchForm,
  CertificationSearchFormState,
} from "@/components/certifications/CertificationSearchForm";

const emptyCertificationFilters: CertificationSearchFormState = {
  submissionType: "",
  programYear: "",
  certificationStatus: "",
};

function getSubmissionType(reportingCalendarId: string | undefined): string {
  if (!reportingCalendarId) return "-";
  if (reportingCalendarId.startsWith("MNTH")) return "Monthly";
  if (reportingCalendarId.startsWith("ANNL")) return "Annual";
  return "-";
}

function getProgramYear(windowStart: string | undefined): string {
  if (!windowStart) return "-";
  const datePart = windowStart.split("T")[0];
  const date = new Date(datePart + "T00:00:00");
  const month = date.getMonth();
  const year = date.getFullYear();
  const startYear = month >= 6 ? year : year - 1;
  return `${startYear}-${startYear + 1}`;
}

function formatDeadline(deadline: string | undefined): string {
  if (!deadline) return "-";
  const datePart = deadline.split("T")[0];
  const d = new Date(datePart + "T00:00:00");
  if (isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export default function CertificationsPage() {
  const leaContext = useRouteContext({ from: "/$leaid" });
  const leaId = useParams({ from: "/$leaid" })?.leaid;
  const navigate = useNavigate({ from: "/$leaid" });
  const { data: certifications, error, isLoading } = useCertifications(leaId);

  const tableData = useMemo(() => {
    if (!certifications) return [];
    return certifications
      .filter((cert) => {
        const id = String(cert.ReportingCalendarId ?? "");
        if (id.startsWith("MNTH")) return leaContext.hasRole("ReportsMonthly");
        if (id.startsWith("ANNL"))
          return leaContext.hasRole("ReportsEOY", "ReportsFall");
        return false;
      })
      .filter((cert) => cert.Status !== "Upcoming")
      .sort((a, b) => {
        const dateA = a.Deadline
          ? new Date(a.Deadline.split("T")[0] + "T00:00:00").getTime()
          : 0;
        const dateB = b.Deadline
          ? new Date(b.Deadline.split("T")[0] + "T00:00:00").getTime()
          : 0;
        return dateB - dateA;
      })
      .map((cert) => {
        return {
          ...cert,
          WindowStartFormatted: parseCertificationMonth(
            String(cert.ReportingCalendarId ?? ""),
            cert.WindowStart
          ),
          SubmissionType: getSubmissionType(
            String(cert.ReportingCalendarId ?? "")
          ),
          ProgramYear: getProgramYear(cert.WindowStart),
          CertificationStatus: normalizeStatus(cert.Status),
          DeadlineFormatted: formatDeadline(cert.Deadline),
          AmendmentEndFormatted: formatDeadline(cert.AmendmentEnd),
        };
      });
  }, [certifications, leaContext]);

  const [appliedFilters, setAppliedFilters] =
    useState<CertificationSearchFormState>(emptyCertificationFilters);

  const submissionTypeOptions = Array.from(
    new Set(tableData.map((c) => c.SubmissionType))
  ).filter((type) => type !== "-");

  const programYearOptions = Array.from(
    new Set(tableData.map((c) => c.ProgramYear))
  );

  const certificationStatusOptions = Array.from(
    new Set(tableData.map((c) => c.CertificationStatus))
  );

  const filteredData = tableData.filter((row) => {
    if (
      appliedFilters.submissionType &&
      row.SubmissionType !== appliedFilters.submissionType
    ) {
      return false;
    }

    if (
      appliedFilters.programYear &&
      row.ProgramYear !== appliedFilters.programYear
    ) {
      return false;
    }

    if (
      appliedFilters.certificationStatus &&
      row.CertificationStatus !== appliedFilters.certificationStatus
    ) {
      return false;
    }

    return true;
  });

  if (isLoading) return null;

  const columns: GridColumnProps[] = [
    {
      id: "WindowStart",
      field: "WindowStartFormatted",
      title: "Submission Cycle",
    },
    {
      id: "SubmissionType",
      field: "SubmissionType",
      title: "Submission Type",
    },
    {
      id: "ProgramYear",
      field: "ProgramYear",
      title: "Program Year",
    },
    {
      id: "Deadline",
      field: "DeadlineFormatted",
      title: "Deadline",
    },
    {
      id: "AmendmentEnd",
      field: "AmendmentEndFormatted",
      title: "Amendment Deadline",
    },
    {
      id: "CertificationStatus",
      field: "CertificationStatus",
      title: "Certification Status",
    },
  ];

  return (
    <LayoutContentArea headingText="Certification Cycles">
      <Card className="card-custom mb-5 shadow-none">
        <CertificationSearchForm
          submissionTypeOptions={submissionTypeOptions}
          programYearOptions={programYearOptions}
          certificationStatusOptions={certificationStatusOptions}
          disabled={isLoading}
          onSearch={setAppliedFilters}
          onClear={() => setAppliedFilters(emptyCertificationFilters)}
        />
      </Card>

      <Card className="mb-3 no-padding card-border">
        <div
          className="text-white p-3 d-flex align-items-center gap-2"
          style={{
            background: "var(--color-p3)",
            fontWeight: 500,
            borderRadius: "0.375rem 0.375rem 0px 0px",
          }}
        >
          <span>List of Cycles</span>
        </div>
        <KendoTableClientState
          key={[
            appliedFilters.submissionType,
            appliedFilters.programYear,
            appliedFilters.certificationStatus,
          ].join("-")}
          data={filteredData}
          columns={columns}
          error={error ?? null}
          filterable={false}
          onRowClick={(event) => {
            const cert = event.dataItem;
            if (!cert.ReportingCalendarId) return;
            navigate({
              to: "/$leaid/certification-cycles/$certificationid",
              params: {
                leaid: leaId,
                certificationid: String(cert.ReportingCalendarId),
              },
            });
          }}
        />
      </Card>
    </LayoutContentArea>
  );
}
